import type { DealSummaryPdfStageMarks } from './dealSummaryPdfRenderTiming';

export const PDF_PAINT_PROXY = 'iframeOnLoad+doubleRaf';

export type PdfRenderTrigger = 'gtlRefresh' | 'directLoad';

const segment = (from: number | undefined, to: number | undefined): number | undefined => {
    if (from === undefined || to === undefined) {
        return undefined;
    }
    const duration = to - from;
    return duration >= 0 ? duration : undefined;
};

const sumDefined = (values: (number | undefined)[]): number =>
    values.reduce<number>((total, value) => (value === undefined ? total : total + value), 0);

export type PdfRenderAttributesInput = {
    marks: DealSummaryPdfStageMarks;
    paintCompleteTime: number;
    pdfFetchAttempts: number;
};

export const buildPdfRenderAttributes = ({
    marks,
    paintCompleteTime,
    pdfFetchAttempts
}: PdfRenderAttributesInput): Record<string, unknown> | undefined => {
    const systemPdfPaintMs = segment(marks.pdfUrlReceived, paintCompleteTime);
    if (systemPdfPaintMs === undefined) {
        return undefined;
    }

    const systemSocketToModalMs = segment(marks.socketEventReceived, marks.refreshModalShown);
    const humanModalToRefreshClickMs = segment(marks.refreshModalShown, marks.refreshRequested);
    const systemDealRefetchMs = segment(marks.refreshRequested, marks.dealFetched);
    const systemDealToPdfFetchMs = segment(marks.dealFetched, marks.pdfFetchRequested);
    const systemPdfFetchMs = segment(marks.pdfFetchRequested, marks.pdfUrlReceived);

    const trigger: PdfRenderTrigger = marks.socketEventReceived === undefined ? 'directLoad' : 'gtlRefresh';

    return {
        systemTotalMs: sumDefined([
            systemSocketToModalMs,
            systemDealRefetchMs,
            systemDealToPdfFetchMs,
            systemPdfFetchMs,
            systemPdfPaintMs
        ]),
        systemSocketToModalMs,
        systemDealRefetchMs,
        systemDealToPdfFetchMs,
        systemPdfFetchMs,
        systemPdfPaintMs,

        humanTotalMs: humanModalToRefreshClickMs ?? 0,
        humanModalToRefreshClickMs,
        includesHumanWaitTime: humanModalToRefreshClickMs !== undefined,

        wallClockIncludingHumanMs: segment(marks.socketEventReceived, paintCompleteTime),

        trigger,
        pdfFetchAttempts,
        paintProxy: PDF_PAINT_PROXY
    };
};
